import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

const LANGUAGES = [
  { id: 'typescript' as const, label: 'TypeScript', ext: '.ts', icon: 'TS' },
  { id: 'python' as const, label: 'Python', ext: '.py', icon: 'PY' },
  { id: 'go' as const, label: 'Go', ext: '.go', icon: 'GO' },
];

const PRODUCTS = [
  { id: '', label: 'All Products' },
  { id: 'psa', label: 'PSA' },
  { id: 'crm', label: 'CRM' },
  { id: 'books', label: 'Books' },
  { id: 'cmdb', label: 'CMDB' },
  { id: 'rmm', label: 'RMM' },
  { id: 'security', label: 'Security' },
];

type Language = 'typescript' | 'python' | 'go';

interface GenerateResult {
  language: string;
  file_name: string;
  code: string;
  products: string[];
  resource_count: number;
}

const QUICK_START: Record<Language, string> = {
  typescript: `import { BridgeClient } from "./bridge-sdk";

const client = new BridgeClient({
  baseUrl: "https://api.theonebridge.app",
  apiKey: "brk_your_api_key_here",
});

// List tickets with pagination
const tickets = await client.listTickets({ page: 1, page_size: 25 });
console.log(\`Found \${tickets.total} tickets\`);

// Create a new company
const company = await client.createCompany({
  name: "Acme Corp",
  phone: "555-0100",
});

// Auto-paginate through all contacts
for await (const batch of client.paginate(client.listContacts.bind(client))) {
  for (const contact of batch) {
    console.log(contact.first_name, contact.last_name);
  }
}`,
  python: `from bridge_sdk import BridgeClient

with BridgeClient(
    base_url="https://api.theonebridge.app",
    api_key="brk_your_api_key_here",
) as client:
    # List tickets with pagination
    tickets = client.list_tickets(page=1, page_size=25)
    print(f"Found {tickets.total} tickets")

    # Create a new company
    company = client.create_company({
        "name": "Acme Corp",
        "phone": "555-0100",
    })

    # Auto-paginate through all contacts
    for batch in client.paginate(client.list_contacts):
        for contact in batch:
            print(contact["first_name"], contact["last_name"])`,
  go: `package main

import (
    "fmt"
    "log"

    "github.com/theonefamily/bridge-sdk-go/bridge"
)

func main() {
    client := bridge.NewClient(
        "https://api.theonebridge.app",
        "brk_your_api_key_here",
    )

    // List tickets with pagination
    tickets, err := client.ListTickets(&bridge.ListParams{
        Page: 1, PageSize: 25,
    })
    if err != nil {
        log.Fatal(err)
    }
    fmt.Printf("Found %d tickets\\n", tickets.Total)

    // Create a new company
    company, err := client.CreateCompany(map[string]interface{}{
        "name":  "Acme Corp",
        "phone": "555-0100",
    })
    if err != nil {
        log.Fatal(err)
    }
    fmt.Println("Created:", company["name"])
}`,
};

export default function SDKPage() {
  const [language, setLanguage] = useState<Language>('typescript');
  const [product, setProduct] = useState('');
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    generate();
  }, [language, product]);

  async function generate() {
    setLoading(true);
    setError('');
    try {
      const res = await api.post<GenerateResult>('/sdk/generate', {
        language,
        ...(product ? { product } : {}),
      });
      setResult(res);
    } catch (err: any) {
      setError(err.message || 'Failed to generate SDK');
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!result) return;
    await navigator.clipboard.writeText(result.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleDownload() {
    if (!result) return;
    const blob = new Blob([result.code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.file_name;
    a.click();
    URL.revokeObjectURL(url);
  }

  const langInfo = LANGUAGES.find(l => l.id === language)!;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>SDK Generator</h1>
        <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
          Generate typed API clients for The One Bridge gateway in TypeScript, Python, or Go.
        </p>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {/* Language Tabs */}
        <div style={{ display: 'flex', background: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.id}
              onClick={() => setLanguage(lang.id)}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderRight: '1px solid #e5e7eb',
                background: language === lang.id ? ACCENT : '#fff',
                color: language === lang.id ? '#fff' : '#374151',
                fontWeight: language === lang.id ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <span style={{
                fontFamily: 'monospace',
                fontWeight: 700,
                fontSize: '0.7rem',
                background: language === lang.id ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                padding: '0.125rem 0.375rem',
                borderRadius: '0.25rem',
              }}>{lang.icon}</span>
              {lang.label}
            </button>
          ))}
        </div>

        {/* Product Filter */}
        <select
          value={product}
          onChange={e => setProduct(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            background: '#fff',
            fontSize: '0.875rem',
            color: '#374151',
            cursor: 'pointer',
          }}
        >
          {PRODUCTS.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ padding: '0.75rem 1rem', background: '#fef2f2', color: '#dc2626', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      {/* Stats Bar */}
      {result && (
        <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#6b7280' }}>
          <span>Products: <strong style={{ color: '#111827' }}>{result.products.join(', ')}</strong></span>
          <span>Resources: <strong style={{ color: '#111827' }}>{result.resource_count}</strong></span>
          <span>File: <strong style={{ color: '#111827' }}>{result.file_name}</strong></span>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          onClick={handleCopy}
          disabled={!result || loading}
          style={{
            padding: '0.5rem 1rem',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            cursor: result ? 'pointer' : 'default',
            color: copied ? '#059669' : '#374151',
            fontWeight: copied ? 600 : 400,
          }}
        >
          {copied ? 'Copied!' : 'Copy to Clipboard'}
        </button>
        <button
          onClick={handleDownload}
          disabled={!result || loading}
          style={{
            padding: '0.5rem 1rem',
            background: ACCENT,
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            cursor: result ? 'pointer' : 'default',
            fontWeight: 600,
          }}
        >
          Download {langInfo.label} SDK
        </button>
      </div>

      {/* Code Preview */}
      <div style={{
        background: '#1e1e1e',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        marginBottom: '2rem',
      }}>
        <div style={{
          padding: '0.5rem 1rem',
          background: '#2d2d2d',
          borderBottom: '1px solid #3d3d3d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: '#9ca3af', fontSize: '0.8rem', fontFamily: 'monospace' }}>
            {result?.file_name || `bridge-sdk${langInfo.ext}`}
          </span>
          {loading && <span style={{ color: ACCENT, fontSize: '0.75rem' }}>Generating...</span>}
        </div>
        <pre style={{
          padding: '1rem',
          margin: 0,
          color: '#d4d4d4',
          fontSize: '0.8rem',
          lineHeight: 1.6,
          fontFamily: '"Fira Code", "Cascadia Code", "JetBrains Mono", "SF Mono", Consolas, monospace',
          overflow: 'auto',
          maxHeight: 500,
          whiteSpace: 'pre',
          tabSize: 2,
        }}>
          {loading ? 'Generating SDK...' : (result?.code || 'Select a language to generate the SDK.')}
        </pre>
      </div>

      {/* Quick Start */}
      <div style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb',
        }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Quick Start — {langInfo.label}</h2>
        </div>
        <pre style={{
          padding: '1rem',
          margin: 0,
          background: '#1e1e1e',
          color: '#d4d4d4',
          fontSize: '0.8rem',
          lineHeight: 1.6,
          fontFamily: '"Fira Code", "Cascadia Code", "JetBrains Mono", "SF Mono", Consolas, monospace',
          overflow: 'auto',
          maxHeight: 400,
          whiteSpace: 'pre',
          tabSize: 2,
        }}>
          {QUICK_START[language]}
        </pre>
      </div>

      {/* Install Instructions */}
      <div style={{
        marginTop: '1.5rem',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '1rem',
      }}>
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 600 }}>Installation</h2>
        {language === 'typescript' && (
          <div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.5rem' }}>
              Download the SDK file and add it to your project. No additional dependencies required (uses native fetch).
            </p>
            <pre style={{ background: '#f3f4f6', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.8rem', margin: 0, fontFamily: 'monospace' }}>
              {`# Download via Bridge UI or curl\ncurl -H "X-API-Key: brk_..." https://api.theonebridge.app/api/sdk/download/typescript -o bridge-sdk.ts`}
            </pre>
          </div>
        )}
        {language === 'python' && (
          <div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.5rem' }}>
              Download the SDK file and install the httpx dependency.
            </p>
            <pre style={{ background: '#f3f4f6', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.8rem', margin: 0, fontFamily: 'monospace' }}>
              {`pip install httpx\n# Download the SDK\ncurl -H "X-API-Key: brk_..." https://api.theonebridge.app/api/sdk/download/python -o bridge_sdk.py`}
            </pre>
          </div>
        )}
        {language === 'go' && (
          <div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.5rem' }}>
              Download the SDK file into your Go project. Uses only the standard library.
            </p>
            <pre style={{ background: '#f3f4f6', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.8rem', margin: 0, fontFamily: 'monospace' }}>
              {`mkdir -p bridge\ncurl -H "X-API-Key: brk_..." https://api.theonebridge.app/api/sdk/download/go -o bridge/bridge_sdk.go`}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
